import { useCallback, useEffect, useRef, useState } from 'react';
import { atom, selector, useRecoilValue } from 'recoil';
import {
  warehouseSelected,
  warehouseSelectedId,
} from '../../store/warehouse.state';
import { DatePickerSettings } from '../../store/warehouse.types';
import useUpdateWarehouseExtraSettings from '../../warehouse/hooks/useUpdateWarehouseExtraSettings';

// Re-export the type for convenience
export type { DatePickerSettings } from '../../store/warehouse.types';

const defaultDatePickerSettings: DatePickerSettings = {
  enableShiftTypeSelectors: false,
  maxShiftsToShow: 5,
  shiftTypes: [],
  enableRelativeTimeSelectors: true,
  enableAbsoluteTimeSelectors: true,
};

// Warehouse-specific DatePicker settings selector
const warehouseDatePickerSettingsSelector = selector<DatePickerSettings>({
  key: 'warehouse-datepicker-settings',
  get: ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    if (!warehouseId) return defaultDatePickerSettings;

    // This will be populated by the warehouse extra settings
    return warehouseDatePickerSettingsState;
  },
});

// Internal state that gets updated by the warehouse settings
let warehouseDatePickerSettingsState = defaultDatePickerSettings;

// Atom to trigger re-renders when settings change
const datePickerSettingsAtom = atom<DatePickerSettings>({
  key: 'datepicker-settings-atom',
  default: defaultDatePickerSettings,
});

export const useDatePickerSettings = () => {
  const warehouseId = useRecoilValue(warehouseSelectedId);
  const warehouse = useRecoilValue(warehouseSelected);
  const [updateWarehouseExtraSettings] = useUpdateWarehouseExtraSettings();
  const [settings, setSettings] = useState<DatePickerSettings>(
    defaultDatePickerSettings,
  );
  const [localWarehouseId, setLocalWarehouseId] = useState<string | null>(null);
  const isSavingRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load settings from warehouse extra settings
  useEffect(() => {
    // Don't reload settings while we're saving to prevent circular updates
    if (!isSavingRef.current) {
      const incomingSettings = warehouse?.extraSettings?.datePickerSettings;
      const hasIncomingSettings = !!incomingSettings;

      console.log('📥 Loading warehouse date picker settings:', {
        hasIncomingSettings,
        incomingShiftsCount: incomingSettings?.shiftTypes?.length || 0,
        currentShiftsCount: settings.shiftTypes.length,
        isSaving: isSavingRef.current,
      });

      if (hasIncomingSettings) {
        const loadedSettings = {
          ...defaultDatePickerSettings,
          ...incomingSettings,
        };

        // Only update if the incoming settings are different and not missing data we have locally
        const shouldUpdate =
          JSON.stringify(loadedSettings) !== JSON.stringify(settings);
        if (shouldUpdate) {
          console.log('🔄 Updating settings from warehouse');
          setSettings(loadedSettings);
          warehouseDatePickerSettingsState = loadedSettings;
        }
      } else {
        // Only reset to defaults if we don't have any local settings
        if (settings.shiftTypes.length === 0) {
          console.log('🔄 Resetting to default settings');
          setSettings(defaultDatePickerSettings);
          warehouseDatePickerSettingsState = defaultDatePickerSettings;
        }
      }
    } else {
      console.log('⏸️ Skipping warehouse settings reload (currently saving)');
    }
  }, [warehouse?.extraSettings?.datePickerSettings]);

  // Debounced save function to prevent rapid updates
  const debouncedSave = useCallback(
    (settingsToSave: DatePickerSettings) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        if (!warehouseId) return;

        isSavingRef.current = true;
        try {
          await updateWarehouseExtraSettings({
            patch: {
              datePickerSettings: settingsToSave,
            },
          });
        } catch (error) {
          console.error('Failed to save DatePicker settings:', error);
        } finally {
          // Reset the saving flag after a short delay to allow warehouse update to complete
          setTimeout(() => {
            isSavingRef.current = false;
          }, 100);
        }
      }, 300); // 300ms debounce
    },
    [warehouseId, updateWarehouseExtraSettings],
  );

  // Track warehouse ID changes (but don't auto-save)
  useEffect(() => {
    if (!warehouseId) return;

    if (localWarehouseId !== warehouseId) {
      setLocalWarehouseId(warehouseId);
    }
  }, [warehouseId, localWarehouseId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const updateSettings = useCallback((updates: Partial<DatePickerSettings>) => {
    setSettings(prevSettings => ({ ...prevSettings, ...updates }));
  }, []);

  const saveSettings = useCallback(async () => {
    if (!warehouseId) return;

    console.log('💾 Manual save triggered for settings:', settings);
    await debouncedSave(settings);
  }, [warehouseId, settings, debouncedSave]);

  return {
    settings,
    updateSettings,
    saveSettings,
    hasShiftTypes: settings.shiftTypes.length > 0,
    hasEnabledShiftTypeSelectors:
      settings.enableShiftTypeSelectors && settings.shiftTypes.length > 0,
  };
};

export default useDatePickerSettings;
